import React, { useState, useEffect } from 'react';
import { Modal, ModalHeader, ModalBody, ModalFooter, Button } from 'reactstrap'; // Assuming reactstrap is used
import { RSelect } from '../../../components/Component'; // Assuming RSelect is a common component
import { getAllTags } from '../../../services/tagsApi'; // Adjusted path
import { axiosPrivate } from '../../../utils/Utils'; // Assuming axiosPrivate is in utils/Utils
import { toast } from 'react-toastify'; // For displaying notifications

const EditUserTagsModal = ({ isOpen, toggle, currentUserTags, userId, onTagsUpdate, updateUserMutation }) => {
    const [allTags, setAllTags] = useState([]);
    const [selectedTags, setSelectedTags] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        if (isOpen) {
            const fetchTags = async () => {
                try {
                    setLoading(true);
                    setError('');
                    const tagsData = await getAllTags(axiosPrivate); // Pass axios instance
                    setAllTags(tagsData.map(tag => ({ value: tag.name, label: tag.name, id: tag.id }))); // Assuming tag object has id and name
                } catch (err) {
                    console.error("Failed to fetch tags:", err);
                    setError("Failed to load tags. Please try again.");
                    toast.error("Failed to load tags.");
                } finally {
                    setLoading(false);
                }
            };
            fetchTags();
        }
    }, [isOpen]);

    useEffect(() => {
        if (currentUserTags && allTags.length > 0) {
            const preselected = allTags.filter(tagOption => currentUserTags.includes(tagOption.value));
            setSelectedTags(preselected);
        } else {
            setSelectedTags([]);
        }
    }, [currentUserTags, allTags]);

    const handleSave = async () => {
        setLoading(true);
        setError('');
        const selectedTagNames = selectedTags.map(tag => tag.value);

        const handleSuccess = () => {
            toast.success("User tags updated successfully!");
            if (onTagsUpdate) {
                onTagsUpdate(); // Callback to refresh profile data
            }
            toggle(); // Close modal
        };

        const handleError = (err) => {
            console.error("Failed to update user tags:", err);
            const errorMessage = err.response?.data?.message || "Failed to update tags. Please try again.";
            setError(errorMessage);
            toast.error(errorMessage);
        };

        try {
            updateUserMutation(
                { userId: userId, payload: { tags: selectedTagNames } },
                {
                    onSuccess: handleSuccess,
                    onError: handleError,
                    onSettled: () => setLoading(false),
                }
            );
        } catch (err) {
            // This catch block might be redundant if the mutation handles all errors,
            // but it's here as a fallback.
            console.error("Error invoking mutation:", err);
            const errorMessage = err.response?.data?.message || "An unexpected error occurred.";
            setError(errorMessage);
            toast.error(errorMessage);
            setLoading(false);
        }
    };

    const handleCancel = () => {
        toggle();
    };

    const onSelectChange = (selectedOptions) => {
        setSelectedTags(selectedOptions || []);
    };

    return (
        <Modal isOpen={isOpen} toggle={toggle}>
            <ModalHeader toggle={toggle}>Edit User Tags</ModalHeader>
            <ModalBody>
                {error && <div className="alert alert-danger">{error}</div>}
                {loading && !allTags.length && <p>Loading tags...</p>}
                {!loading && allTags.length > 0 && (
                    <RSelect
                        options={allTags}
                        isMulti
                        value={selectedTags}
                        onChange={onSelectChange}
                        placeholder="Select tags..."
                    />
                )}
                {!loading && !allTags.length && !error && <p>No tags available to select.</p>}
            </ModalBody>
            <ModalFooter>
                <Button color="light" onClick={handleCancel} disabled={loading}>
                    Cancel
                </Button>
                <Button color="primary" onClick={handleSave} disabled={loading || selectedTags.length === 0}>
                    {loading ? "Saving..." : "Save"}
                </Button>
            </ModalFooter>
        </Modal>
    );
};

export default EditUserTagsModal;